import {
    WIDGET_DEFINITIONS,
    buildEmbedCode,
    type WebsitePlatform,
} from '@flexbyt/shared';
import { SourceGlyph, cx } from '@flexbyt/widget-preview';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';
import { Breadcrumbs } from '../components/site/Breadcrumbs.js';
import { ButtonAnchor, ButtonLink, SiteLink, buttonClass } from '../components/ui/button.js';
import {
    ArrowIcon,
    BrowserWindow,
    CheckIcon,
    HERO_PILL,
    HeroBackdrop,
    HeroFacts,
} from '../components/ui/hero.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Heading, MarkPhrase, Text } from '../components/ui/type.js';
import {
    DeviceFrame,
    DeviceSwitcher,
    PREVIEW_DEVICES,
    PreviewStage,
    ScaledInline,
    type PreviewDeviceId,
} from '../components/widgets/PreviewStage.js';
import { WidgetShowcase } from '../components/WidgetShowcase.js';
import { sourcePagePath } from '../content/integrations.js';
import { SAMPLE_SETS, fill } from '../content/sample-sets.js';
import { PLATFORM_CONTENT, SEO_PLATFORMS } from '../content/platforms.js';
import { CONNECT_SOURCES } from '../content/sources.js';
import {
    WIDGET_CONTENT,
    WIDGET_COUNT_WORD,
    defaultCountFor,
    generatorPath,
    widgetContentBySlug,
    widgetPath,
    type WidgetContent,
} from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * One widget, in depth.
 *
 * ===========================================================================
 * Data-driven without being thin
 * ===========================================================================
 * Six pages from one route, which is the shape that usually produces six thin
 * duplicates — the same four paragraphs with a noun swapped. The defence is in
 * `content/widgets.ts`: every page's headline, intro, use cases, "pick
 * something else when…" and FAQs are written for that layout and say things
 * that are only true of it. The route arranges them; it does not generate them.
 *
 * The interactive preview and the install snippet are genuinely shared, because
 * those *are* the same job on every page — and both are produced by the real
 * product code rather than described.
 */
export const Route = createFileRoute('/widgets/$slug')({
  loader: ({ params }) => {
    const widget = widgetContentBySlug(params.slug);
    // A 404 rather than a redirect to the gallery: `/widgets/review-timeline`
    // is not a mistyped layout, it is a layout that does not exist, and saying
    // so is more useful than silently landing somewhere else.
    if (!widget) throw notFound();
    return { widget };
  },
  head: ({ loaderData }) => {
    const widget = loaderData?.widget;
    if (!widget) return {};

    const { meta, links } = seo({
      title: `${widget.seoName} review widget`,
      description: widget.intro,
      path: widgetPath(widget),
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Widgets', path: '/widgets' },
              { name: widget.seoName, path: widgetPath(widget) },
            ])
          ),
        },
        {
          /*
            FAQ structured data, from the same array the page renders.

            Safe because the questions and answers on the page *are* these —
            schema describing content a visitor cannot find is the kind that
            gets a site penalised, and the only way to guarantee they agree is
            to build both from one source.
          */
          type: 'application/ld+json',
          children: jsonLd(faqSchema(widget.faqs)),
        },
      ],
    };
  },
  component: WidgetPage,
});

function WidgetPage() {
  const { widget } = Route.useLoaderData();
  const definition = WIDGET_DEFINITIONS[widget.type];
  const others = WIDGET_CONTENT.filter((entry) => entry.slug !== widget.slug);

  return (
    <>
      {/* ------------------------------------------------------------ Hero */}
      <Section
        size="tight"
        width="wide"
        className="relative isolate overflow-x-clip pt-10 md:pt-14"
      >
        <HeroBackdrop />

        <Breadcrumbs
          trail={[
            { name: 'Home', path: '/' },
            { name: 'Widgets', path: '/widgets' },
            { name: widget.seoName, path: widgetPath(widget) },
          ]}
        />

        {/*
          Top-aligned, not centred: the widget is the tallest thing here, and
          centring it against the copy pushed its top down and its bottom out
          of the window — the spotlight and popup, which need the whole page
          mock-up to make sense, lost their lower half.
        */}
        <div className="mt-10 grid items-start gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="min-w-0 lg:col-span-6">
            <p className={cx('rj-rise', HERO_PILL)}>
              <span className="grid size-6 place-items-center rounded-full bg-brand/10 text-brand">
                <svg
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="size-3"
                  aria-hidden
                >
                  <rect x="1.5" y="1.5" width="5.5" height="7" rx="1.5" />
                  <rect x="9" y="1.5" width="5.5" height="4" rx="1.5" />
                  <rect x="1.5" y="10.5" width="5.5" height="4" rx="1.5" />
                  <rect x="9" y="7.5" width="5.5" height="7" rx="1.5" />
                </svg>
              </span>
              {widget.seoName} widget
              <span aria-hidden className="text-ink-300">
                ·
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden className="size-1.5 rounded-full bg-positive-600" />
                Free to build
              </span>
            </p>

            <Heading
              level={1}
              size="hero"
              className="rj-rise rj-delay-1 mt-6 leading-[0.98] font-extrabold tracking-[-0.045em]"
            >
              <MarkPhrase text={widget.headline} phrase={widget.markPhrase} />
            </Heading>

            <Text
              size="lead"
              className="rj-rise rj-delay-2 mt-6 max-w-2xl sm:text-xl sm:leading-9"
            >
              {widget.intro}
            </Text>

            <div className="rj-rise rj-delay-3 mt-9 flex flex-col gap-3 sm:flex-row">
              <ButtonLink
                to={generatorPath(widget)}
                size="lg"
                className="h-14 px-8"
                onClick={() =>
                  track('widget_selected', {
                    widget: widget.type,
                    surface: 'widget-page-hero',
                  })
                }
              >
                Build this widget free
                <ArrowIcon />
              </ButtonLink>
              <ButtonAnchor
                href={startUrl(`widget-page-${widget.type}`)}
                variant="secondary"
                size="lg"
                className="h-14 px-8"
                onClick={() =>
                  track('hero_cta_clicked', { surface: `widget-page-${widget.type}` })
                }
              >
                Start free
              </ButtonAnchor>
            </div>

            <HeroFacts
              className="rj-rise rj-delay-4 mt-7"
              facts={[
                'Free plan, no card',
                'Updates itself as reviews come in',
                'Works on Wix, Webflow, WordPress and Squarespace',
              ]}
            />

            <div className="rj-rise rj-delay-4 mt-8 flex items-center gap-3">
              <span className="flex -space-x-1.5" aria-hidden>
                {CONNECT_SOURCES.map((source) => (
                  <span
                    key={source.id}
                    className="grid size-7 place-items-center rounded-full bg-white shadow-[0_1px_2px_rgba(10,10,11,0.08),0_4px_10px_-4px_rgba(20,40,120,0.25)]"
                  >
                    <SourceGlyph source={source.id} className="size-3.5" />
                  </span>
                ))}
              </span>
              <p className="text-caption text-ink-500">
                Reviews from Google, Trustpilot, Yelp, Tripadvisor, Airbnb and the app stores
              </p>
            </div>
          </div>

          <div className="rj-rise rj-delay-3 min-w-0 lg:col-span-6">
            <HeroWidget widget={widget} />
          </div>
        </div>
      </Section>

      {/* ------------------------------------------------------- Use cases */}
      <Section tone="raised" aria-labelledby="uses-heading">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16">
          <div>
            <Heading level={2} id="uses-heading">
              When to use the {widget.seoName.toLowerCase()}.
            </Heading>
            <Text className="mt-4 max-w-prose">{definition.description}</Text>

            {/*
              The honest half. A page that only lists reasons to choose this
              layout is a page a reader discounts entirely — and pointing
              somebody at the right layout costs nothing, because it is another
              page on this site.
            */}
            <div className="mt-8 border-l-2 border-ink-200 pl-5">
              <p className="text-caption font-semibold text-ink-950">
                Pick something else when…
              </p>
              <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                {widget.insteadWhen}
              </p>
            </div>
          </div>

          <ul className="space-y-6">
            {widget.useCases.map((useCase, index) => (
              <li key={useCase.title} className="flex gap-5">
                <span className="nums shrink-0 text-body-sm font-semibold text-ink-300">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <p className="text-title text-ink-950">{useCase.title}</p>
                  <p className="mt-1.5 max-w-prose text-body-sm text-ink-600">
                    {useCase.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* ------------------------------------------------------ Responsive */}
      <Section aria-labelledby="responsive-heading">
        <Measure>
          <Heading level={2} id="responsive-heading">
            It fits the space it is given.
          </Heading>
          <Text className="mt-4 max-w-prose">
            The same widget, at three widths. It reflows rather than shrinking —
            no horizontal scrolling, no eight-point type on a phone.
          </Text>
        </Measure>

        <ResponsiveShowcase widget={widget} />
      </Section>

      {/* --------------------------------------------------------- Install */}
      <Section tone="raised" aria-labelledby="install-heading">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-16">
          <div>
            <Heading level={2} id="install-heading">
              Installing it takes a minute.
            </Heading>
            <Text className="mt-4 max-w-prose">
              Publish the reviews you want, copy the snippet, and paste it where
              the reviews should appear. It updates itself as new reviews come
              in — you never touch the code again.
            </Text>

            <div className="mt-8">
              <p className="text-caption font-semibold text-ink-950">
                Works on
              </p>
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                {SEO_PLATFORMS.map((platform) => (
                  <li key={platform}>
                    <SiteLink
                      to={`/for/${PLATFORM_CONTENT[platform].slug}`}
                      className="text-body-sm text-ink-600 underline-offset-[3px] hover:text-ink-950 hover:underline"
                    >
                      {PLATFORM_CONTENT[platform].name}
                    </SiteLink>
                  </li>
                ))}
                <li className="text-body-sm text-ink-500">
                  and any site you can paste HTML into
                </li>
              </ul>
            </div>
          </div>

          <InstallSnippet widget={widget} />
        </div>
      </Section>

      {/* ---------------------------------------------------------- Import */}
      <Section aria-labelledby="sources-heading">
        <Measure>
          <Heading level={2} id="sources-heading">
            Where the reviews come from.
          </Heading>
          <Text className="mt-4 max-w-prose">
            The {widget.seoName.toLowerCase()} shows whatever is in your
            ReviewsJet library, whichever platform each review arrived from.
          </Text>
        </Measure>

        <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
          {CONNECT_SOURCES.map((source) => (
            <li key={source.id}>
              <SiteLink
                to={sourcePagePath(source.id)}
                className={buttonClass({ variant: 'secondary', size: 'sm' })}
              >
                {source.name} reviews
              </SiteLink>
            </li>
          ))}
        </ul>
      </Section>

      {/* ------------------------------------------------------------- FAQ */}
      <Section tone="raised" aria-labelledby="faq-heading">
        <Measure>
          <Heading level={2} id="faq-heading">
            Questions about the {widget.seoName.toLowerCase()}.
          </Heading>
        </Measure>

        <dl className="mt-10 max-w-text">
          {widget.faqs.map((faq, index) => (
            <div key={faq.q} className={cx(index > 0 && 'mt-8 border-t border-ink-150 pt-8')}>
              <dt className="text-title text-ink-950">{faq.q}</dt>
              <dd className="mt-2.5 text-body text-ink-600">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* ---------------------------------------------------- Other widgets */}
      <Section aria-labelledby="others-heading">
        <Heading level={2} id="others-heading" size="title">
          The other five layouts
        </Heading>

        <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((other) => (
            <li key={other.slug}>
              <SiteLink
                to={widgetPath(other)}
                className={cx(
                  'group flex h-full flex-col rounded-lg px-4 py-3.5 ring-1 ring-ink-150',
                  'transition-colors duration-[var(--duration-fast)]',
                  'hover:bg-ink-25 hover:ring-ink-300'
                )}
              >
                <span className="text-body-sm font-medium text-ink-950">
                  {other.seoName}
                </span>
                <span className="mt-1 text-caption text-ink-500">
                  {other.bestFor}
                </span>
              </SiteLink>
            </li>
          ))}
        </ul>
      </Section>

      {/* ----------------------------------------------------------- Close */}
      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Build your {widget.seoName.toLowerCase()} now.
          </Heading>
          <Text className="mt-4 text-white/70">
            No account needed to try it. Make it look right first, then decide.
          </Text>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonLink
              to={generatorPath(widget)}
              variant="inverse"
              size="lg"
              onClick={() =>
                track('generator_completed', {
                  widget: widget.type,
                  surface: 'widget-page-close',
                })
              }
            >
              Build this widget free
            </ButtonLink>
            <ButtonLink to="/widgets" variant="inverse-ghost" size="lg">
              Compare all {WIDGET_COUNT_WORD}
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}

/**
 * The same widget at three widths.
 *
 * ===========================================================================
 * Desktop is live; the tablet and the phone are iframes
 * ===========================================================================
 * The layouts size themselves from **viewport** media queries, so a 375px
 * `<div>` with the renderer inside it draws the desktop arrangement — three
 * columns of review cards crushed into a phone's width. That is not a bug in
 * the renderer (a widget on a customer's site should react to the screen, not
 * to the box somebody put it in), but it means the narrow widths cannot be
 * shown in the page's own document. An iframe has a viewport of its own: a
 * 375px frame *is* a 375px screen to the media queries inside it, so those two
 * show the real phone and tablet layouts rather than a shrunken desktop one.
 *
 * The desktop view needs none of that — the page *is* a desktop viewport — and
 * an iframe there only cost things: it was a fixed 1200px document scaled to
 * fit, so it stopped short of the section's edges on a wide screen and sat at
 * 1200px in the middle of a full-screen dialog; its reviews opened their modal
 * inside a 560px frame; and it was a second document to load for the one view
 * everybody sees first. So desktop is the renderer, live, at whatever width
 * the section has, with the same reviews the frames are given.
 *
 * `loading="lazy"` on the frames because they are below the fold and each is
 * a document of its own.
 */
function ResponsiveShowcase({ widget }: { widget: (typeof WIDGET_CONTENT)[number] }) {
  const [device, setDevice] = useState<PreviewDeviceId>('desktop');
  const count = Math.min(defaultCountFor(widget.type) || 6, 6);
  const src = `/preview/widget?type=${widget.type}&count=${count}`;
  const active = PREVIEW_DEVICES.find((entry) => entry.id === device)!;
  // What `/preview/widget` draws by default, so switching device never
  // switches reviews.
  const reviews = fill(SAMPLE_SETS[0]!.reviews, count);

  return (
    <PreviewStage
      className="mt-10"
      title={`${widget.seoName} widget on ${active.label.toLowerCase()}`}
      toolbar={
        <>
          <DeviceSwitcher value={device} onChange={setDevice} />
          {/*
            The frames are exact widths and say so. Desktop is live at whatever
            width the section has, so a number there would be a claim about a
            box that does not exist.
          */}
          {device !== 'desktop' && (
            <span className="nums text-caption text-ink-400">
              {active.width}px
            </span>
          )}
        </>
      }
    >
      {/*
        A function rather than an element, so the enlarged copy can fill the
        dialog. `DeviceFrame` measures whichever container it lands in — the two
        copies are in boxes of very different sizes, and the one in the dialog
        used to inherit the page's measurement and draw a page-sized frame in a
        full-viewport window.
      */}
      {({ fullscreen }) =>
        device === 'desktop' ? (
          <div className="w-full">
            <WidgetShowcase type={widget.type} reviews={reviews} />
          </div>
        ) : (
          <DeviceFrame
            src={src}
            title={`${widget.seoName} widget at ${active.width} pixels wide`}
            width={active.width}
            fill={fullscreen}
          />
        )
      }
    </PreviewStage>
  );
}

/**
 * The snippet, produced by the product's own builder.
 *
 * `buildEmbedCode` is the function the dashboard's install sheet calls, so what
 * is shown here cannot drift from what a customer is actually given — including
 * the detail that WordPress gets a shortcode rather than a script tag, which is
 * the kind of thing a hand-written marketing example gets wrong and then
 * silently misleads people about for a year.
 *
 * The id is obviously a placeholder. A real-looking one would be somebody's
 * actual workspace.
 */
function InstallSnippet({ widget }: { widget: (typeof WIDGET_CONTENT)[number] }) {
  const platforms: { platform: WebsitePlatform; label: string }[] = [
    { platform: 'other', label: 'Any website' },
    { platform: 'wordpress', label: 'WordPress' },
  ];

  return (
    <div className="space-y-4">
      {platforms.map(({ platform, label }) => (
        <div key={platform}>
          <p className="text-caption font-semibold text-ink-950">{label}</p>
          <pre className="mt-2 overflow-x-auto rounded-lg bg-ink-950 p-4 text-micro leading-relaxed text-white/85">
            <code>
              {buildEmbedCode({
                widgetType: widget.type,
                embedId: 'your-site-id',
                platform,
              })}
            </code>
          </pre>
        </div>
      ))}

      <p className="text-caption text-ink-500">
        ReviewsJet fills in your site id when you install — nothing to type.
      </p>
    </div>
  );
}

/**
 * The widget itself, on a stand-in for the visitor's own page.
 *
 * The real renderer rather than a picture, as everywhere else on this page —
 * drawn at a desktop width and scaled into the window, so a grid is a grid
 * and not three columns crushed into a phone's worth of space. Faded out at the
 * bottom rather than cut: a masonry wall is taller than any hero should be,
 * and a hard edge through a card reads as a rendering bug.
 */
function HeroWidget({ widget }: { widget: WidgetContent }) {
  // Nine whatever the layout: `defaultCount` is 0 ("all of them") for several
  // layouts, and `fill` reads that as none — an empty spotlight in the hero.
  const reviews = fill(SAMPLE_SETS[0]!.reviews, 9);
  const floating = widget.type === 'spotlight' || widget.type === 'popup';
  // One review is never taller than the window, and fading it cut its author.
  const whole = floating || widget.type === 'quote';

  return (
    <div aria-hidden className="relative mx-auto max-w-2xl pb-8 lg:max-w-none">
      <div className="absolute inset-4 -z-10 rounded-[3rem] bg-[radial-gradient(closest-side,rgba(31,91,240,0.14),transparent)] blur-2xl" />

      <BrowserWindow className="pointer-events-none select-none">
        <div
          className={cx(
            'relative overflow-hidden',
            /*
              The spotlight and popup are a page with something on top of it,
              so they get their whole mock-up at its own height — never cropped
              — and a narrower render width, so it scales up rather than down.
              Everything else is a section of reviews, which can fade out.
            */
            floating
              ? 'p-3'
              : whole
                ? 'px-4 py-5 sm:px-5'
                : 'max-h-[31rem] px-4 pt-4 [mask-image:linear-gradient(to_bottom,black_78%,transparent)] sm:px-5 sm:pt-5'
          )}
        >
          <ScaledInline width={floating ? 720 : 960}>
            <WidgetShowcase type={widget.type} reviews={reviews} />
          </ScaledInline>
        </div>
      </BrowserWindow>

      <div className="rj-float absolute right-3 bottom-0 flex items-center gap-2 rounded-full bg-ink-950 py-2 pr-4 pl-2 text-caption font-medium text-white shadow-[0_24px_48px_-20px_rgba(10,10,11,0.6)] sm:-right-4">
        <span className="grid size-6 place-items-center rounded-full bg-white/15">
          <CheckIcon className="size-3 text-white" />
        </span>
        Live preview, not a screenshot
      </div>
    </div>
  );
}
